<template>
  <header class="page-header">
    <div class="header-content">
      <!-- Logo Section -->
      <div class="logo-section">
        <div class="logo-icon">
          <img src="@/assets/logo.png" alt="Captain Leo's Maps Logo" class="logo-image" />
        </div>
        <div class="logo-text">
          <h1 class="app-title">Captain Leo's Maps</h1>
          <span class="app-subtitle">Travel Explorer</span>
        </div>
      </div>

      <!-- Navigation/Actions -->
      <div class="header-actions">
        <!-- Leaderboards/App Link - Dynamic based on current route -->
        <router-link
          :to="isOnLeaderboardsPage ? '/app' : '/leaderboards'"
          class="nav-link leaderboards-link"
        >
          <i :class="isOnLeaderboardsPage ? 'fas fa-map-marked-alt' : 'fas fa-trophy'"></i>
          <span>{{ isOnLeaderboardsPage ? 'Explore' : 'Leaderboards' }}</span>
        </router-link>

        <!-- User section when authenticated -->
        <div v-if="isAuthenticated && user" class="user-section">
          <router-link :to="`/profile/${user.name}`" class="profile-link">
            <div class="user-info">
              <span class="user-name">{{ user.name || 'User' }}</span>
              <div class="user-stats">
                <span class="stat-item">
                  <i class="fas fa-trophy"></i>
                  {{ user.quizStats?.total_score || 0 }}
                </span>
              </div>
            </div>
          </router-link>
          <button @click="handleLogout" class="logout-btn">
            <i class="fas fa-sign-out-alt"></i>
          </button>
        </div>
        
        <!-- Login/Register buttons when not authenticated -->
        <div v-else class="auth-actions">
          <button @click="openAuthModal('login')" class="auth-btn login-btn">
            <i class="fas fa-sign-in-alt"></i>
            <span>Sign In</span>
          </button>
          <button @click="openAuthModal('register')" class="auth-btn register-btn">
            <i class="fas fa-user-plus"></i>
            <span>Join</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Decorative Elements -->
    <div class="header-decoration">
      <div class="decoration-dot"></div>
      <div class="decoration-dot"></div>
      <div class="decoration-dot"></div>
    </div>
    
    <!-- Auth Modal -->
    <AuthModal 
      :isOpen="authModalOpen" 
      :mode="authMode"
      @close="closeAuthModal"
      @success="onAuthSuccess" 
    />
  </header>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AuthModal from './AuthModal.vue'

export default {
  name: 'PageHeader',
  components: {
    AuthModal
  },
  data() {
    return {
      authModalOpen: false,
      authMode: 'login' // 'login' or 'register'
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'user']),
    isOnLeaderboardsPage() {
      return this.$route.path === '/leaderboards'
    }
  },
  async mounted() {
    // Try to load user profile on app start if token exists
    if (this.isAuthenticated) {
      await this.fetchProfile()
    }
    // Check if we should show auth modal from query params
    this.checkAuthQueryParams()

    // Listen for global auth modal request (e.g., from StarButton)
    window.addEventListener('show-auth-modal', this.handleShowAuthModal)
  },
  beforeUnmount() {
    // Clean up event listener
    window.removeEventListener('show-auth-modal', this.handleShowAuthModal)
  },
  watch: {
    '$route'() {
      // Watch for changes in route query params
      this.checkAuthQueryParams()
    }
  },
  methods: {
    ...mapActions('auth', ['logout', 'fetchProfile']),

    checkAuthQueryParams() {
      // Check if showAuth query param is present
      if (this.$route.query.showAuth === 'true' && !this.isAuthenticated) {
        this.openAuthModal('login')
      }
    },

    openAuthModal(mode) {
      this.authMode = mode
      this.authModalOpen = true
    },

    closeAuthModal() {
      this.authModalOpen = false
    },

    async onAuthSuccess() {
      this.closeAuthModal()

      // Wait a tick for auth state to update
      await this.$nextTick()

      // Check if there's a redirect URL in query params
      if (this.$route.query.redirect) {
        const redirectPath = this.$route.query.redirect
        // Clean up query params and redirect
        this.$router.push(redirectPath)
      }
    },

    async handleLogout() {
      await this.logout()
    },

    handleShowAuthModal() {
      // Open login modal when requested globally (e.g., from StarButton)
      if (!this.isAuthenticated) {
        this.openAuthModal('login')
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 30px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo-icon {
  width: 90px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  animation: bounce 3s ease-in-out infinite;
}

.logo-image {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.logo-text {
  display: flex;
  flex-direction: column;
}

.app-title {
  font-family: 'Chewy', cursive;
  font-size: 32px;
  font-weight: 400;
  margin: 0;
  text-shadow: 2px 2px 27px rgba(0, 0, 0, 0.5), 0px 0px 10px rgba(255, 215, 0, 0.3);
  background: linear-gradient(45deg, #FFD700, #FFA500);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 1px;
}

.app-subtitle {
  font-size: 14px;
  font-weight: 400;
  opacity: 0.9;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 20px;
}

.stats-badge {
  background: rgba(255, 255, 255, 0.15);
  padding: 12px 20px;
  border-radius: 25px;
  display: flex;
  align-items: center;
  gap: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-weight: 500;
  transition: all 0.3s ease;
}

.stats-badge:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.stats-badge i {
  font-size: 16px;
}

.header-decoration {
  position: absolute;
  top: 10px;
  right: 30px;
  display: flex;
  gap: 8px;
}

.decoration-dot {
  width: 8px;
  height: 8px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

.decoration-dot:nth-child(2) {
  animation-delay: 0.5s;
}

.decoration-dot:nth-child(3) {
  animation-delay: 1s;
}

@keyframes pulse {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
}

/* Navigation Link */
.nav-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(255, 215, 0, 0.2);
  border: 1px solid rgba(255, 215, 0, 0.4);
  border-radius: 20px;
  color: white;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.nav-link:hover {
  background: rgba(255, 215, 0, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
}

.nav-link i {
  font-size: 16px;
  color: #FFD700;
}

.leaderboards-link.router-link-active {
  background: rgba(255, 215, 0, 0.35);
  border-color: rgba(255, 215, 0, 0.6);
}

/* Authentication styles */
.user-section {
  display: flex;
  align-items: center;
  gap: 15px;
  background: rgba(255, 255, 255, 0.1);
  padding: 10px 15px;
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.profile-link {
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease;
}

.profile-link:hover {
  opacity: 0.8;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.user-name {
  font-weight: 600;
  font-size: 14px;
  color: white;
}

.user-stats {
  display: flex;
  gap: 10px;
  margin-top: 2px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.stat-item i {
  font-size: 12px;
  color: #FFD700;
}

.logout-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.auth-actions {
  display: flex;
  gap: 10px;
}

.auth-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.auth-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.login-btn {
  background: rgba(76, 175, 80, 0.2);
  border-color: rgba(76, 175, 80, 0.4);
}

.login-btn:hover {
  background: rgba(76, 175, 80, 0.3);
}

.register-btn {
  background: rgba(33, 150, 243, 0.2);
  border-color: rgba(33, 150, 243, 0.4);
}

.register-btn:hover {
  background: rgba(33, 150, 243, 0.3);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .page-header {
    padding: 12px 16px;
  }
  
  .header-content {
    flex-direction: row;
    gap: 10px;
  }
  
  .logo-section {
    gap: 10px;
  }
  
  .logo-icon {
    width: 50px;
    height: 50px;
  }
  
  .logo-image {
    width: 40px;
    height: 40px;
  }
  
  .app-title {
    font-size: 20px;
  }
  
  .app-subtitle {
    font-size: 10px;
  }
  
  .stats-badge {
    padding: 8px 12px;
    font-size: 12px;
  }
  
  .header-decoration {
    display: none;
  }
  
  .user-section {
    gap: 8px;
    padding: 6px 10px;
  }
  
  .user-name {
    font-size: 12px;
  }
  
  .stat-item {
    font-size: 10px;
  }
  
  .logout-btn {
    width: 26px;
    height: 26px;
  }
  
  .auth-actions {
    gap: 6px;
  }
  
  .auth-btn {
    padding: 6px 10px;
    font-size: 11px;
  }
  
  .auth-btn span {
    display: none;
  }

  .nav-link {
    padding: 8px 12px;
    font-size: 12px;
  }

  .nav-link span {
    display: none;
  }

  .nav-link i {
    font-size: 18px;
  }
}

/* iPad specific styles */
@media (min-width: 768px) and (max-width: 1366px) {
  .page-header {
    padding: 10px 16px;
  }
  
  .logo-icon {
    width: 45px;
    height: 45px;
  }
  
  .logo-image {
    width: 36px;
    height: 36px;
  }
  
  .app-title {
    font-size: 22px;
  }
  
  .app-subtitle {
    font-size: 11px;
  }
  
  .stats-badge {
    padding: 8px 14px;
    font-size: 13px;
  }
}

/* iPad landscape mode (extra compact) */
@media (min-width: 1024px) and (max-width: 1366px) and (orientation: landscape) {
  .page-header {
    padding: 6px 12px;
  }
  
  .logo-icon {
    width: 36px;
    height: 36px;
  }
  
  .logo-image {
    width: 28px;
    height: 28px;
  }
  
  .app-title {
    font-size: 18px;
  }
  
  .app-subtitle {
    font-size: 9px;
  }
  
  .stats-badge {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  .header-decoration {
    display: none;
  }
}

/* Small mobile screens (under 480px) */
@media (max-width: 480px) {
  .page-header {
    padding: 8px 12px;
  }
  
  .logo-icon {
    width: 40px;
    height: 40px;
  }
  
  .logo-image {
    width: 32px;
    height: 32px;
  }
  
  .app-title {
    font-size: 16px;
  }
  
  .app-subtitle {
    font-size: 9px;
  }
  
  .stats-badge {
    padding: 6px 10px;
    font-size: 11px;
  }
  
  .stats-badge span {
    display: none;
  }
}
</style>

